import { autoinject, BindingEngine, computedFrom, transient } from "aurelia-framework";
import { Router, RouterEvent } from "aurelia-router";
import { I18N } from "aurelia-i18n";
import { NotificationClientService } from "../../../../services/basics/notification-client.service";
import { EventAggregator } from "aurelia-event-aggregator";
import { ValidationsClientService } from "../../../../services/validations/validations-client.service";
import { RightShipQiRouterConstants } from "../../../../configuration/rightshipqi-router.constants";
import { StepModel } from "../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { BaseValidationResponse } from "../../../../models/validations/validations.models";
import {
    AddSubtaskRequest,
    SafetyRatingReviewClientModel,
    SafetyRatingReviewStatusEnum,
    SafetyRatingReviewOutcomeStepEnum,
    SafetyRatingReviewStepEnum,
    SafetyRatingReviewTaskClientModel
} from "../../../../models/vetting/vetting.models";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../constants/authentication/claims";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../constants/basics/rightship-dialog.constants";
import { Reflection } from "../../../../utilities/reflection";
import { ActiveMenuItemsService } from "../../../../services/basics/active-menu-items.service";
import { UserClientService } from "../../../../services/authentication/user-client.service";
import { EntityStatusChangeModel, SignalFunctions } from "../../../../services/basics/signal-functions";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { LookupValue, LookupValueRequest } from "../../../../models/lookups/lookup.models";
import { LookupValueQuery } from "../../../../constants/modules/lookup.constants";
import { LookupClientService } from "../../../../services/lookups/lookup-client.service";
import { CustomerService as CommonCustomerService } from "@rightship-tech/common-ui/services/basic/customer-client.service";
import { Assignment } from "../../../components/assignment/assignment";
import { CacheClientService, CacheLocation } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import * as Enumerable from "linq";
import * as moment from "moment";
import { ClipboardCopyService } from "../../../../utilities/clipboard-copy-service";
import { Correspondence } from "../common-steps/correspondence/correspondence";
import { EntityItem } from "../entity-item";
import { EntityComments } from "../../common/entity-notes/entity-notes";
import { TitleWithCountHelper } from "../../../../helpers/title-with-count-helper";
import { SafetyScoreReviewOverview } from "./tabs/safety-score-review-overview";
import { SafetyScoreReviewReviewDetails } from "./tabs/safety-score-review-review-details";
import { SafetyScoreReviewDocuments } from "./tabs/safety-score-review-documents";
import { SafetyScoreReviewHistoryTrail } from "./tabs/safety-score-review-history-trail";
import { SafetyRatingReviewService } from "../../../../services/vetting/safety-rating-review.service";
import { ValidationController, ValidationControllerFactory, ValidationRules } from "aurelia-validation";
import { CustomValidationRenderer } from "../../../../client-validation-system/custom-validation-renderer";
import { EmailHistory } from "../common-steps/email-history/email-history";
import { RatingBaseEntity } from "../common-steps/base-rating-entity";
import { ManageVesselService } from "../../../../services/vessels/manage-vessel.service";
import { Guid } from "../../../../utilities/guid";
import { CompCorrespondenceModel } from "../../../../models/vessels/vessel.models";

type SafetyRatingReviewSource = 'vessels' | 'activities';

@autoinject
@transient()
export class SafetyRatingReview extends RatingBaseEntity<SafetyRatingReviewClientModel> {

    public shouldRemoveCacheOnDetached: boolean = true;
    public readonly: boolean = true;
    public canViewSafetyRatingReviewDocuments: boolean;
    public safetyRatingReviewStatuses: LookupValue[];
    public canChangeSafetyRatingReviewStatus: boolean;
    public canAssignSafetyRatingReview: boolean;
    public businessUnitId: number;
    public showVesselMoreDetailsDrawer: boolean;
    public canViewSafetyRatingReviewCorrespondence: boolean;
    public canViewSafetyRatingReviewEmailHistory: boolean;
    public canAcceptFirstReview: boolean;
    public canAcceptSecondReview: boolean;
    public entityItem: EntityItem;
    public correspondenceSafetyRatingReviewTaskId: number;
    public correspondenceSubtaskId: number;
    public assignment: Assignment;

    @computedFrom(Reflection.getPropertyPath<SafetyRatingReview>(v => v.router.currentInstruction))
    public get previousInstructionTitle(): string {
        const source: SafetyRatingReviewSource = this._params.source;

        switch (source) {
            case 'vessels':
                return `${this.i18N.tr('vessel-text')}`;
            case 'activities':
                return `${this.i18N.tr('tasks-text')}`;
            default:
                {
                    const prevInstruction = this.router.currentInstruction.previousInstruction;
                    return prevInstruction
                        ? prevInstruction.config.title
                        : this.i18N.tr('back-text');
                }
        }
    }

    @computedFrom(Reflection.getPropertyPath<SafetyRatingReview>(x => x.item), Reflection.getPropertyPath<SafetyRatingReview>(x => x.item.built))
    public get age() {
        if (this.item) {
            if (this.item.built) {
                return (Math.floor(moment().diff(this.item.built, 'year', true) * 10) / 10).toFixed(1) + " y/o"
            } else {
                return ""
            }
        } else {
            return null;
        }
    }

    @computedFrom(Reflection.getPropertyPath<SafetyRatingReview>(v => v.item), Reflection.getPropertyPath<SafetyRatingReview>(v => v.item.safetyRatingReviewStatusId))
    get isClosed() {
        return this.item && (
            this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.Accepted2 ||
            this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.Canceled ||
            this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.AcceptedFromSystem);
    }

    async canDeactivate() {
        const rfis = await this.cacheClient.get<{ [index: number]: AddSubtaskRequest }>(`RFI-SRR-${this.item.safetyRatingReviewId}`, CacheLocation.SessionStorage, false);
        if (rfis && !this.isClosed) {
            const result = await new Promise<boolean>((resolve) => {
                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AreYouSureExitDialog, {
                    yes: () => {
                        resolve(true);
                    },
                    no: () => {
                        resolve(false);
                    }
                },
                    true, false, false, 'right');

            });
            return result;
        }
        else if (rfis && this.isClosed) {
            await this.cacheClient.remove(`RFI-SRR-${this.item.safetyRatingReviewId}`, CacheLocation.SessionStorage, false);
        }

        return true;
    }

    @computedFrom(Reflection.getPropertyPath<SafetyRatingReview>(v => v.item.vesselId))
    public get vesselUrl() {
        return this.item && this.item.vesselId ? this.router.generate(RightShipQiRouterConstants.RouteNames.VesselDetails, {
            id: this.item.vesselId,
        }) : null;
    }

    validator: ValidationController;
    constructor(protected router: Router,
        protected bindingEngine: BindingEngine, private dialogClientService: DialogClientService,
        protected i18N: I18N, private userClaimsService: UserClaimsService,
        protected notificationService: NotificationClientService,
        protected eventAggregator: EventAggregator,
        protected safetyScoreReviewOverview: SafetyScoreReviewOverview,
        protected safetyScoreReviewReviewDetails: SafetyScoreReviewReviewDetails,
        protected safetyScoreReviewDocuments: SafetyScoreReviewDocuments,
        protected entityComments: EntityComments,
        protected signalClientService: SignalClientService,
        protected validationsClientService: ValidationsClientService,
        protected userClientService: UserClientService,
        protected manageVesselService: ManageVesselService,
        private cacheClient: CacheClientService,
        private safetyRatingReviewService: SafetyRatingReviewService,
        private customerService: CommonCustomerService,
        private correspondence: Correspondence,
        protected emailHistory: EmailHistory,
        private safetyScoreReviewHistoryTrail: SafetyScoreReviewHistoryTrail,
        lookupClientService: LookupClientService,
        private activeMenuItemsService: ActiveMenuItemsService,
        private clipboardCopyService: ClipboardCopyService,
        validationControllerFactory: ValidationControllerFactory) {
        super(router, bindingEngine, i18N, notificationService, eventAggregator, validationsClientService, signalClientService, manageVesselService);
        this.validator = validationControllerFactory.createForCurrentScope();
        this.validator.addRenderer(new CustomValidationRenderer());

        this.safetyScoreReviewReviewDetails.assignToMe = this.assignToMe.bind(this);
        this.safetyScoreReviewReviewDetails.viewCorrespondence = this.viewCorrespondence.bind(this);
        this.safetyScoreReviewOverview.viewCorrespondence = this.viewCorrespondenceBySubtaskId.bind(this);
        this.safetyScoreReviewOverview.reloadItemFunc = (async () => {
            await this.reload();
        }).bind(this);
        this.safetyScoreReviewReviewDetails.reloadItemFunc = (async (safetyRatingReviewTaskId: number) => {
            await this.reload();
            if (safetyRatingReviewTaskId)
                this.viewReviewBySafetyRatingReviewTaskId(safetyRatingReviewTaskId, false);
        }).bind(this);
        this.canViewSafetyRatingReviewDocuments = this.userClaimsService.hasPermission(Claims.CanViewSafetyRatingReviewDocuments);
        this.canAssignSafetyRatingReview = this.userClaimsService.hasPermission(Claims.CanAssignSafetyRatingReview);
        this.canViewSafetyRatingReviewCorrespondence = this.userClaimsService.hasPermission(Claims.CanViewSafetyRatingReviewCorrespondence);
        this.canViewSafetyRatingReviewEmailHistory = this.userClaimsService.hasPermission(Claims.CanViewSafetyRatingReviewEmailHistory);
        this.canAcceptFirstReview = this.userClaimsService.hasPermission(Claims.CanAcceptFirstReview);
        this.canAcceptSecondReview = this.userClaimsService.hasPermission(Claims.CanAcceptSecondReview);

        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.SafetyRatingReviewStatuses;
        lookupClientService.getLookup(request).then(x => {
            this.safetyRatingReviewStatuses = x.activeValues;
        });

        this.customerService.getBusinessUnitId().then(x => {
            this.businessUnitId = x;
        });

        eventAggregator.subscribe(RouterEvent.Processing, (x) => {
            if (x && x.instruction && x.instruction.config) {
                const name = x.instruction.config.name;
                this.shouldRemoveCacheOnDetached = name != RightShipQiRouterConstants.RouteNames.PSC && name != RightShipQiRouterConstants.RouteNames.Incident;
            }
        });
    }

    reload = () => {
        return new Promise<void>((resolve, reject) => {
            this.getItem(this.getItemId()).then(item => {
                this.item = item;
                this.initializeItemOverrideForUpdate(this.item);
                setTimeout(() => {
                    this.refreshSteps();
                    resolve();
                });
            }, reject);
        });
    }

    protected initializeItemOverrideForUpdate(item: SafetyRatingReviewClientModel) {
        const ordered = Enumerable.from(item.safetyRatingReviewStatusHistory).orderBy(x => x.createdDate).toArray();
        let wasAcceptable1 = false;

        for (const i of ordered) {
            if (i.statusId == SafetyRatingReviewStatusEnum.Accepted1) {
                wasAcceptable1 = true;
            }
            else if (i.statusId != SafetyRatingReviewStatusEnum.InProgress &&
                i.statusId != SafetyRatingReviewStatusEnum.SafetyRatingReviewReassignment) {
                wasAcceptable1 = false;
            }
        }
        this.canChangeSafetyRatingReviewStatus = this.canAcceptFirstReview || (wasAcceptable1 && this.canAcceptSecondReview);
    }

    viewCorrespondenceBySubtaskId(subtaskId: number) {
        this.correspondenceSafetyRatingReviewTaskId = null;
        this.correspondenceSubtaskId = subtaskId;
        const step = this.steps.filter(x => x.step == SafetyRatingReviewStepEnum.SafetyRatingReviewCorrespondence)[0];
        this.entityItem.currentStep = step;
    }

    viewReviewBySafetyRatingReviewTaskId(safetyRatingReviewTaskId: number, navigate: boolean) {
        this.correspondenceSafetyRatingReviewTaskId = safetyRatingReviewTaskId;
        this.correspondenceSubtaskId = null;
        const step = this.steps.filter(x => x.step == SafetyRatingReviewStepEnum.SafetyRatingReview)[0];
        if (this.entityItem && navigate)
            this.entityItem.currentStep = step;
        const task = Enumerable.from(this.safetyScoreReviewReviewDetails.safetyRatingReviewTasks).selectMany<SafetyRatingReviewTaskClientModel>(x => x.value).firstOrDefault(x => x.safetyRatingReviewTaskId == safetyRatingReviewTaskId);
        this.safetyScoreReviewReviewDetails.viewTask(task);
    }

    viewCorrespondence(currentTask: SafetyRatingReviewTaskClientModel) {
        this.correspondenceSubtaskId = null;
        this.correspondenceSafetyRatingReviewTaskId = currentTask.safetyRatingReviewTaskId;
        const step = this.steps.filter(x => x.step == SafetyRatingReviewStepEnum.SafetyRatingReviewCorrespondence)[0];
        this.entityItem.currentStep = step;
    }

    @computedFrom(Reflection.getPropertyPath<SafetyRatingReview>(v => v.item), Reflection.getPropertyPath<SafetyRatingReview>(v => v.item.safetyRatingReviewStatusId))
    public get statusIsFinal(): boolean {
        return this.item && (this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.Accepted2 ||
            this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.Canceled ||
            this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.AcceptedFromSystem);
    }

    @computedFrom(Reflection.getPropertyPath<SafetyRatingReview>(v => v.item), Reflection.getPropertyPath<SafetyRatingReview>(v => v.item.safetyRatingReviewStatusId), Reflection.getPropertyPath<SafetyRatingReview>(v => v.canAcceptSecondReview))
    public get disableChangeStatus(): boolean {
        const ordered = Enumerable.from(this.item.safetyRatingReviewStatusHistory).orderBy(x => x.createdDate).toArray();
        return this.item && this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.Accepted1 && !this.canAcceptSecondReview && ordered[ordered.length - 1].userId == this.userClientService.currentUser.id;
    }

    async assignToMe() {
        if (this.assignment) {
            await this.assignment.assignToMe();
            this.item.safetyRatingReviewStatusId = SafetyRatingReviewStatusEnum.InProgress;
            this.item.assignedUserId = this.userClientService.currentUser.id;
            // don't think this is needed
            // this.refreshSafetyRatingReviewStatus({ safetyRatingReviewId: this.item.safetyRatingReviewId, status: SafetyRatingReviewStatusEnum.InProgress, user: this.userClientService.currentUser.userName });
        }
    }

    protected onActivation() {
        this.activeMenuItemsService.setActiveFromMenuItem(this.i18N.tr('tasks-text'));
    }

    protected getListRoute(): string {
        return RightShipQiRouterConstants.RouteNames.Activities;
    }

    protected getSteps(): StepModel[] {
        return [
            {
                lazyLoad: true,
                title: this.i18N.tr("safety-score-review-overview-text"),
                step: SafetyRatingReviewStepEnum.SafetyRatingReviewOverview,
                vm: this.safetyScoreReviewOverview,
                show: true,
                onClick: () => {
                    //this.safetyScoreReviewOverview.showAllRecentActivity();
                }
            },
            {
                lazyLoad: true,
                title: this.i18N.tr("review-text"),
                step: SafetyRatingReviewStepEnum.SafetyRatingReview,
                vm: this.safetyScoreReviewReviewDetails,
                show: this.userClaimsService.hasPermission(Claims.CanViewSafetyRatingReviewReview),
                getModel: () => {
                    return { item: this.item, assignment: this.assignment }
                }
            },
            {
                lazyLoad: true,
                title: () => TitleWithCountHelper.getTitle(this.i18N.tr("safety-score-review-documents-text"), this.item.documentsCount),
                step: SafetyRatingReviewStepEnum.SafetyRatingReviewDocuments,
                vm: this.safetyScoreReviewDocuments,
                show: this.canViewSafetyRatingReviewDocuments
            },
            {
                lazyLoad: true,
                title: () => TitleWithCountHelper.getTitle("Correspondences", this.correspondence.correspondenceCount ?? this.item.correspondenceCount),
                step: SafetyRatingReviewStepEnum.SafetyRatingReviewCorrespondence,
                vm: this.correspondence,
                getModel: () => {
                    return this.getCorrespondenceModel()
                },
                show: () => this.canViewSafetyRatingReviewCorrespondence && this.item ? true : false
            },
            {
                lazyLoad: true,
                title: "Email History",
                step: SafetyRatingReviewStepEnum.SafetyRatingReviewEmailHistory,
                vm: this.emailHistory,
                getModel: () => {
                    return {
                        item: this.item
                    }
                },
                show: () => this.canViewSafetyRatingReviewEmailHistory && this.item ? true : false
            },
            {
                lazyLoad: true,
                title: () => TitleWithCountHelper.getTitle(this.i18N.tr("safet-rating-review-private-notes-text"), this.item.privateNotesCount),
                step: SafetyRatingReviewStepEnum.SafetyRatingReviewComments,
                vm: this.entityComments,
                show: this.item.safetyRatingReviewId > 0 && this.userClaimsService.hasPermission(Claims.CanViewSafetyRatingReviewNotes),
                getModel: () => ({
                    canSubmit: true,
                    entityTypeId: this.item.entityType,
                    entityId: this.item.safetyRatingReviewId,
                    item: this.item
                })
            },
            {
                lazyLoad: true,
                title: this.i18N.tr("history-trail-text"),
                step: SafetyRatingReviewStepEnum.SafetyRatingReviewHistoryTrail,
                vm: this.safetyScoreReviewHistoryTrail,
                show: this.item.safetyRatingReviewId > 0 && this.userClaimsService.hasPermission(Claims.CanViewSafetyRatingReviewHistoryTrail)
            }
        ]
    }

    private getCorrespondenceModel(): CompCorrespondenceModel {
        return {
            initialSubtaskId: this.correspondenceSubtaskId,
            //correspondenceSafetyRatingReviewTaskId: this.correspondenceSafetyRatingReviewTaskId,
            correspondenceEntity: {
                entityId: this.item.safetyRatingReviewId,
                entityTypeId: this.item.entityType,
                isInconsistentState: false,
                requestedBy: this.item.requestedBy,
                liaseWithShipManagers: null,
                assignment: {
                    assignedTo: this.item.assignedUserId,
                    assignedToUserName: this.item.assignedTo,
                    assignToMe: this.assignToMe.bind(this),
                    isAssignedToMe: () => this.item.assignedUserId == this.userClientService.currentUser.id,
                }
            },
            name: "Review",
            showAddRFI: this.item && this.userClaimsService.hasPermission(Claims.CanAddSafetyRatingReviewRfi) && !this.isClosed,
            hasOutcome: this.isClosed,
            emails: [],
            newDocumentAdded: () => {
                this.item.documentsCount += 1;
            }
        };
    }

    protected getItem(id: number): Promise<SafetyRatingReviewClientModel> {
        return this.safetyRatingReviewService.getSafetyRatingReview(id, true);
    }

    protected getItemId(): number {
        return this.item ? this.item.safetyRatingReviewId : null;
    }

    protected saveItem(): Promise<BaseValidationResponse> {
        return null;
    }

    protected getUpdateRoute(): string {
        return RightShipQiRouterConstants.RouteNames.SafetyScoreReview;
    }

    protected getEntityType(): EntityTypes {
        return EntityTypes.SafetyRatingReview;
    }

    protected initializeItemForCreate(): SafetyRatingReviewClientModel | Promise<SafetyRatingReviewClientModel> {
        const review: SafetyRatingReviewClientModel = {
            docCompanyId: null,
            documentOfComplianceEmail: null,
            phoneNumbers: [],
            otherComments: null,
            safetyRatingReviewStatusId: null,
            safetyRatingReviewStatus: null,
            vesselId: null,
            safetyRatingReviewId: null,
            yearOfConstruction: null,
            dateRequested: null,
            built: null,
            deadWeightTonnage: null,
            requestedBy: null,
            assignedUserId: null,
            internationalMaritimeOrganizationNumber: null,
            europeanNumberOfIdentification: null,
            vehicleIdentificationNumber: null,
            vesselName: null,
            vesselType: null,
            safetyRatingReviewComments: null,
            safetyRatingReviewTasks: [],
            safetyRatingReviewStatusHistory: [],
            rating: null,
            starRating: null,
            starRatingAtSafetyRatingReviewOutcome: null,
            safetyRatingTypeAtSafetyRatingReviewOutcome: null,
            starRatingAtSafetyRatingReviewAfterOutcome: null,
            safetyRatingTypeAtSafetyRatingReviewAfterOutcome: null,
            safetyRatingReviewCorrespondenceEmails: [],
            safetyRatingReviewRequiresMoreInformationEmails: [],
            identifier: Guid.generateGuid(),
            incidentGracePeriodsOutcome: [],
            incidentGracePeriodsAfterOutcome: [],
        }
        return review;
    }

    public navigateBack() {
        this.router.navigateBack();
    }

    getValidationRules() {
        const rules = ValidationRules
            .ensure<SafetyRatingReviewTaskClientModel, string>(x => x.vettingComment)
            .required()
            .withMessage("Comment is required")
            .when(x => x.vetSuperSafetyRatingReviewTaskOutcomeId == SafetyRatingReviewOutcomeStepEnum.Pass && x.safetyRatingReviewTaskOutcomeId == SafetyRatingReviewOutcomeStepEnum.Fail);

        return rules.rules;
    }


    viewCriteriaByVetTaskId(task: SafetyRatingReviewTaskClientModel) {
        const step = this.steps.filter(x => x.step == SafetyRatingReviewStepEnum.SafetyRatingReview)[0];
        this.entityItem.currentStep = step;
        this.safetyScoreReviewReviewDetails.viewTask(task);
    }

    async openSafetyRatingReviewStatusDialog() {
        if (this.statusIsFinal) return;

        if (this.canChangeSafetyRatingReviewStatus) {
            if (!this.isClosed) {
                const rules = this.getValidationRules();

                const p = this.item.safetyRatingReviewTasks.map(a =>
                    this.validator.validate({
                        object: a,
                        rules: rules
                    }));

                const result = await Promise.all(p);
                if (result.filter(x => !x.valid).length > 0) {
                    const task = Enumerable.from(this.safetyScoreReviewReviewDetails.safetyRatingReviewTasks)
                        .selectMany<SafetyRatingReviewTaskClientModel>(x => x.value)
                        .firstOrDefault(x => x.vetSuperSafetyRatingReviewTaskOutcomeId == SafetyRatingReviewOutcomeStepEnum.Pass && x.safetyRatingReviewTaskOutcomeId == SafetyRatingReviewOutcomeStepEnum.Fail && !x.vettingComment);
                    this.viewCriteriaByVetTaskId(task);
                    return;
                }


                if (this.item.assignedUserId == this.userClientService.currentUser.id || this.isClosed) {
                    const taskOutcomes = this.item.safetyRatingReviewTasks.filter(x => x.vetSuperSafetyRatingReviewTaskOutcomeId).map(x => x.vetSuperSafetyRatingReviewTaskOutcomeId);
                    const unsetRules = this.item.safetyRatingReviewTasks.filter(x => x.vetSuperSafetyRatingReviewTaskOutcomeId != 1 && x.vetSuperSafetyRatingReviewTaskOutcomeId != 2).length;
                    const vetSuperFinished = unsetRules == 0;

                    const tasks = this.item.safetyRatingReviewTasks
                        .filter(x => x.vetSuperSafetyRatingReviewTaskOutcomeId)
                        .filter(x => x.vetSuperSafetyRatingReviewTaskOutcomeId == SafetyRatingReviewOutcomeStepEnum.Fail).map(x => {
                            return {
                                safetyRatingReviewTaskId: x.safetyRatingReviewTaskId,
                                name: x.title,
                                isPassed: x.vetSuperSafetyRatingReviewTaskOutcomeId == SafetyRatingReviewOutcomeStepEnum.Pass,
                                comment: x.vettingComment,
                            };
                        });

                    const reloadItemFunc = async () => {
                        await this.reload()
                        this.entityItem.currentStep = this.steps[0];
                    };
                    const ordered = Enumerable.from(this.item.safetyRatingReviewStatusHistory).orderBy(x => x.createdDate).toArray();
                    let wasAcceptable1 = false;

                    for (const i of ordered) {
                        if (i.statusId == SafetyRatingReviewStatusEnum.Accepted1) {
                            wasAcceptable1 = true;
                        }
                        else if (i.statusId != SafetyRatingReviewStatusEnum.InProgress &&
                            i.statusId != SafetyRatingReviewStatusEnum.SafetyRatingReviewReassignment) {
                            wasAcceptable1 = false;
                        }
                    }

                    this.dialogClientService.show(RightShipDialogServiceConstants.URLs.SafetyScoreReviewStatusDialog,
                        {
                            vetSuperFinished: vetSuperFinished,
                            anyRfi: this.correspondence && this.correspondence.allItems ?
                                this.correspondence.allItems.filter(x => x.isCustomerRFI).length > 0
                                : this.item.anyRfi,
                            passed: taskOutcomes.filter(x => x == SafetyRatingReviewOutcomeStepEnum.Pass).length,
                            failed: taskOutcomes.filter(x => x == SafetyRatingReviewOutcomeStepEnum.Fail).length,
                            noVetSuperOutcome: unsetRules,
                            otherComments: this.item.otherComments,
                            tasks: tasks,
                            wasAcceptable1: wasAcceptable1,
                            canAcceptable1: !wasAcceptable1 && this.canAcceptFirstReview,
                            canAcceptable2: wasAcceptable1 && this.canAcceptSecondReview,
                            safetyRatingReviewId: this.item.safetyRatingReviewId,
                            safetyRatingReviewStatusId: this.item.safetyRatingReviewStatusId,
                            onDraftGenerated: reloadItemFunc,
                            onSubmit: reloadItemFunc,
                            vesselName: this.item.vesselName,
                            internationalMaritimeOrganizationNumber: this.item.internationalMaritimeOrganizationNumber,
                            europeanNumberOfIdentification: this.item.europeanNumberOfIdentification,
                            vehicleIdentificationNumber: this.item.vehicleIdentificationNumber,
                            subtaskTypes: [],
                        }, true, false, false, 'right');
                } else {
                    this.dialogClientService.show(RightShipDialogServiceConstants.URLs.SafetyScoreReviewAssignmentDialog, {
                        name: "Review",
                        submit: this.assignToMe.bind(this),
                        callback: this.openSafetyRatingReviewStatusDialog.bind(this),
                        currentAssignment: this.assignment.assignedEntity.assignedToUserName
                    }, true, false, false);
                }
            }
        }
    }

    protected onSafetyRatingReviewTaskUpdated = (params: { safetyRatingReviewId: number }) => {
        if (this.item.safetyRatingReviewId != params.safetyRatingReviewId) return;

        this.safetyRatingReviewService.getSafetyRatingReviewTasks(this.item.safetyRatingReviewId).then(x => {
            this.item.safetyRatingReviewTasks = x;
            if (this.safetyScoreReviewReviewDetails.currentTask) {
                const id = this.safetyScoreReviewReviewDetails.currentTask.safetyRatingReviewTaskId;
                this.safetyScoreReviewReviewDetails.viewTask(null);
                setTimeout(() => {
                    const task = this.item.safetyRatingReviewTasks.filter(x => x.safetyRatingReviewTaskId == id)[0];
                    this.safetyScoreReviewReviewDetails.viewTask(task);
                })
            }
        });
    }

    onAttached() {

        this.signalClientService.subscribe(SignalFunctions.safetyRatingReviewTaskUpdateChanged, this.onSafetyRatingReviewTaskUpdated);
        this.signalClientService.subscribe(SignalFunctions.safetyRatingReviewHistoryChanged, this.onSafetyRatingReviewHistoryChanged);
        this.signalClientService.subscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.SafetyRatingReview), this.refreshSafetyRatingReviewStatus);

        if (this._params && this._params.subtaskId && this._params.subtaskId > 0) {
            this.viewCorrespondenceBySubtaskId(this._params.subtaskId);
            window.history.replaceState(window.history.state, 'Review', window.location.href.replace(`?subtaskId=${this._params.subtaskId}`, ''));
        }

        if (this._params && this._params.safetyRatingReviewTaskId && this._params.safetyRatingReviewTaskId > 0) {
            this.viewReviewBySafetyRatingReviewTaskId(this._params.safetyRatingReviewTaskId, true);
        }
    }

    onDetached() {
        this.signalClientService.unsubscribe(SignalFunctions.safetyRatingReviewTaskUpdateChanged, this.onSafetyRatingReviewTaskUpdated);
        this.signalClientService.unsubscribe(SignalFunctions.safetyRatingReviewHistoryChanged, this.onSafetyRatingReviewHistoryChanged);
        this.signalClientService.unsubscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.SafetyRatingReview), this.refreshSafetyRatingReviewStatus);
    }


    protected onSafetyRatingReviewHistoryChanged = (params: { safetyRatingReviewIds: number[] }) => {
        if (!params.safetyRatingReviewIds || params.safetyRatingReviewIds.length == 0 || params.safetyRatingReviewIds.filter(x => x == this.item.safetyRatingReviewId).length == 0)
            return;

        this.reload();
    }

    refreshSafetyRatingReviewStatus = (param: EntityStatusChangeModel) => {
        if ((this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.Accepted1
            || this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.Accepted2
            || this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.AcceptedFromSystem
            || this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.Canceled
            || this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.Rejected) && param.statusId != SafetyRatingReviewStatusEnum.InProgress) return;
        if (this.item.safetyRatingReviewId == param.entityId) {
            const safetyRatingReviewStatus = param.statusId ? this.safetyRatingReviewStatuses.filter(x => x.id == param.statusId)[0] : null;
            if (safetyRatingReviewStatus) {
                this.reload();
            }
            else if (this.assignment && param.statusId != SafetyRatingReviewStatusEnum.SafetyRatingReviewReassignment) {
                this.assignment.reload().then(x => this.item.assignedUserId = x.assignedTo);
            }
        }
    }

    onViewSafetyRatingReview = (id: number) => {
        this.safetyRatingReviewService.getSafetyRatingReview(id, true).then(x => {
            this.item = x;
        });
    };

    toggleVesselMoreDetailsDrawer() {
        this.showVesselMoreDetailsDrawer = !this.showVesselMoreDetailsDrawer;
    }

    copy(str: string) {
        this.clipboardCopyService.copy(str);
    }

    getStatus(status: string) {
        status = status.split(' ').join('-').toLowerCase();

        if (status.indexOf('(') != -1) {
            status = status.substr(0, status.indexOf('(') - 1);
        }

        return status;
    }
}